import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import DOMPurify from "dompurify";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { resolveUrl as resolveCoverUrl } from "@/components/CoverUpload";
import ShareButtons from "@/components/ShareButtons";
import { toast } from "sonner";
import { Calendar, ShoppingCart, Youtube, Sparkles, Hourglass, BookOpen, Bell, Check, Home, Globe } from "lucide-react";
import { Input } from "@/components/ui/input";

function youtubeEmbed(url) {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|v=)([\w-]{11})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
}

const LANG_NAMES = {
  en: "English", hi: "Hindi", ta: "Tamil", te: "Telugu", es: "Spanish", fr: "French",
  de: "German", it: "Italian", pt: "Portuguese", ja: "Japanese", ko: "Korean",
  zh: "Chinese", ar: "Arabic", ru: "Russian", bn: "Bengali", mr: "Marathi",
  pa: "Punjabi", gu: "Gujarati", ml: "Malayalam", kn: "Kannada", ur: "Urdu",
  und: "Other",
};

function VideoStrip({ videos, language, onSelect, selectedId }) {
  return (
    <div className="flex gap-4 overflow-x-auto pb-3 -mx-1 px-1 snap-x snap-mandatory" data-testid={`video-strip-${language}`}>
      {videos.map((v) => (
        <button
          key={v.video_id}
          onClick={() => onSelect(v)}
          className="snap-start text-left w-64 flex-shrink-0 rounded-lg overflow-hidden group transition-all"
          style={{
            background: "white",
            border: selectedId === v.video_id ? "2px solid var(--bf-primary)" : "1px solid var(--bf-border)",
          }}
          data-testid={`video-card-${v.video_id}`}
        >
          <div className="relative aspect-video bg-black">
            {v.thumbnail && (
              <img src={v.thumbnail} alt={v.title} className="w-full h-full object-cover group-hover:opacity-90 transition-opacity" />
            )}
            <div className="absolute bottom-2 right-2 px-2 py-0.5 text-xs rounded text-white" style={{ background: "rgba(0,0,0,0.7)" }}>
              <Youtube className="w-3 h-3 inline mr-1" /> Watch
            </div>
          </div>
          <div className="p-3">
            <p className="font-serif text-base line-clamp-2 leading-tight mb-1">{v.title}</p>
            <p className="text-xs" style={{ color: "var(--bf-text-muted)" }}>
              {v.published_at ? new Date(v.published_at).toLocaleDateString() : ""}
            </p>
          </div>
        </button>
      ))}
    </div>
  );
}

export default function PublicBook() {
  const { authorSlug, bookSlug } = useParams();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading");
  const [videosData, setVideosData] = useState(null);
  const [videosStatus, setVideosStatus] = useState("idle");
  const [language, setLanguage] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [subEmail, setSubEmail] = useState("");
  const [subBusy, setSubBusy] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [previewLang, setPreviewLang] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/${authorSlug}/${bookSlug}`);
        setData(data);
        setStatus("ok");
      } catch (e) {
        setStatus("notfound");
      }
    })();
  }, [authorSlug, bookSlug]);

  useEffect(() => {
    if (!data) return;
    (async () => {
      setVideosStatus("loading");
      try {
        const { data: vData } = await api.get(`/public/${authorSlug}/${bookSlug}/videos`);
        setVideosData(vData);
        const langs = vData.languages || [];
        setLanguage(langs[0] || null);
        const firstVid = (vData.videos || [])[0];
        if (firstVid) setSelectedVideo(firstVid);
        setVideosStatus("ok");
      } catch {
        setVideosData({ videos: [], languages: [], configured: false });
        setVideosStatus("error");
      }
    })();
  }, [data, authorSlug, bookSlug]);

  const grouped = useMemo(() => {
    if (!videosData || !videosData.videos) return {};
    return videosData.videos.reduce((acc, v) => {
      const lang = v.language || "und";
      (acc[lang] = acc[lang] || []).push(v);
      return acc;
    }, {});
  }, [videosData]);

  if (status === "loading") {
    return <div className="min-h-screen flex items-center justify-center" data-testid="public-loading">Loading…</div>;
  }
  if (status === "notfound") {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="public-notfound">
        <div className="text-center">
          <h1 className="font-serif text-5xl mb-3">Page not found</h1>
          <p className="mb-6" style={{ color: "var(--bf-text-muted)" }}>This book doesn't exist.</p>
          <Link to="/"><Button variant="outline">Back home</Button></Link>
        </div>
      </div>
    );
  }

  const { author, book, preview_chapters = [], share_url } = data;
  const trailerEmbed = youtubeEmbed(book.youtube_url);
  const isPublished = book.status === "published";
  const buyLinks = [];
  if (book.buy_url) buyLinks.push({ label: "Book your copy", url: book.buy_url, primary: true });
  if (book.ads_url && book.ads_url !== book.buy_url) buyLinks.push({ label: isPublished ? "Featured retailer" : "Pre-order", url: book.ads_url });
  (book.purchase_links || []).forEach((l) => buyLinks.push(l));

  const copyShare = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success("Link copied");
  };

  const subscribe = async (e) => {
    e.preventDefault();
    setSubBusy(true);
    try {
      const { data } = await api.post(`/public/${authorSlug}/${bookSlug}/subscribe`, { email: subEmail });
      setSubscribed(true);
      toast.success(data.already_subscribed ? "You're already subscribed" : "You'll hear from us on launch day");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not subscribe");
    } finally {
      setSubBusy(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--bf-bg)" }} data-testid="public-book-page">
      <div
        className="relative"
        style={{
          backgroundImage:
            "url(https://static.prod-images.emergentagent.com/jobs/e21241af-dc17-4ac1-b5d0-f39caea73c8a/images/55410c45e12b57513424c2e1a6867ae80550bc7bb92f2b3775f48e468dbc0dca.png)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="backdrop-blur-sm bg-white/60">
          <header className="max-w-5xl mx-auto px-6 lg:px-12 pt-10 pb-6 flex justify-between items-center gap-4">
            <Link to="/" className="font-serif text-2xl" data-testid="public-brand">Bookforge</Link>
            <div className="flex items-center gap-4 text-sm flex-wrap justify-end" style={{ color: "var(--bf-text-muted)" }}>
              <Link to={`/${author.slug}`} className="hover:underline hidden sm:inline" data-testid="public-back-to-author">← All by {author.name}</Link>
              <button onClick={copyShare} className="underline" data-testid="public-share-btn">Copy link</button>
              <Link to="/" data-testid="public-book-home-link">
                <Button variant="outline" size="sm" className="h-9">
                  <Home className="w-4 h-4 mr-1.5" /> Home
                </Button>
              </Link>
            </div>
          </header>
          <section className="max-w-5xl mx-auto px-6 lg:px-12 py-16 grid lg:grid-cols-[1fr_240px] gap-10 items-start">
            <div>
              <div className="label-eyebrow mb-5">by {author.name}</div>
              <h1 className="font-serif text-5xl sm:text-6xl mb-5 leading-tight" data-testid="public-book-title">{book.title}</h1>
              {book.theme && (
                <p className="font-serif italic text-xl mb-6 max-w-2xl" style={{ color: "var(--bf-text-muted)" }}>{book.theme}</p>
              )}
              <div className="flex flex-wrap gap-2 mb-5">
                {book.language && book.language !== "en" && (
                  <span className="text-xs px-3 py-1.5 rounded inline-flex items-center gap-1.5" style={{ background: "var(--bf-accent)", color: "white" }} data-testid="public-book-language">
                    <Globe className="w-3 h-3" />
                    {LANG_NAMES[book.language] || book.language.toUpperCase()}
                  </span>
                )}
                {(book.genres || []).map((g) => (
                  <span key={g} className="text-xs px-3 py-1.5 rounded" style={{ background: "var(--bf-surface)", color: "var(--bf-text-muted)" }}>{g}</span>
                ))}
              </div>
              <ShareButtons
                url={typeof window !== "undefined" ? window.location.href : ""}
                title={book.title}
                summary={book.short_summary || book.theme || ""}
              />
            </div>
            {book.cover_image_url && (
              <img
                src={resolveCoverUrl(book.cover_image_url)}
                alt="Book cover"
                className="w-full max-w-[240px] aspect-[2/3] object-cover rounded shadow-md"
                style={{ border: "1px solid var(--bf-border)" }}
                data-testid="public-book-cover"
              />
            )}
          </section>
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-6 lg:px-12 py-14">
        {book.short_summary && (
          <section className="mb-10" data-testid="public-book-summary">
            <div className="label-eyebrow mb-3">Summary</div>
            <p className="prose-book text-xl max-w-3xl">{book.short_summary}</p>
          </section>
        )}

        <Accordion type="multiple" defaultValue={["prelaunch", "postlaunch"]} className="space-y-4" data-testid="lifecycle-accordion">
          {/* PRE-LAUNCH */}
          <AccordionItem
            value="prelaunch"
            className="rounded-lg overflow-hidden border-0"
            style={{ background: "white", border: "1px solid var(--bf-border)" }}
            data-testid="lifecycle-prelaunch"
          >
            <AccordionTrigger className="px-6 py-5 hover:no-underline" data-testid="lifecycle-prelaunch-trigger">
              <div className="flex items-center gap-3">
                <Sparkles className="w-5 h-5" style={{ color: "var(--bf-accent)" }} />
                <div className="text-left">
                  <h2 className="font-serif text-2xl">Pre-launch</h2>
                  <p className="text-xs" style={{ color: "var(--bf-text-muted)" }}>
                    Trailers, behind-the-scenes and the road to launch.
                  </p>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-6 pb-7" data-testid="lifecycle-prelaunch-content">
              <div className="pt-4 space-y-6 border-t" style={{ borderColor: "var(--bf-border)" }}>
                {!isPublished && (
                  <div
                    className="mt-4 p-5 rounded-lg flex flex-col sm:flex-row gap-4 items-start sm:items-center"
                    style={{ background: "var(--bf-surface)", border: "1px solid var(--bf-border)" }}
                    data-testid="prelaunch-notify-widget"
                  >
                    <Bell className="w-6 h-6 flex-shrink-0" style={{ color: "var(--bf-primary)" }} />
                    <div className="flex-1">
                      <p className="font-serif text-xl leading-tight">Notify me when it drops</p>
                      <p className="text-xs" style={{ color: "var(--bf-text-muted)" }}>
                        One email on launch day. No spam, ever.
                      </p>
                    </div>
                    {subscribed ? (
                      <div className="flex items-center gap-2 text-sm" style={{ color: "var(--bf-primary)" }} data-testid="prelaunch-notify-success">
                        <Check className="w-4 h-4" /> You're on the list
                      </div>
                    ) : (
                      <form onSubmit={subscribe} className="flex gap-2 w-full sm:w-auto" data-testid="prelaunch-notify-form">
                        <Input
                          type="email"
                          placeholder="you@example.com"
                          required
                          value={subEmail}
                          onChange={(e) => setSubEmail(e.target.value)}
                          className="h-10 w-full sm:w-64"
                          data-testid="prelaunch-notify-email"
                        />
                        <Button
                          type="submit"
                          disabled={subBusy}
                          className="text-white h-10 px-5"
                          style={{ background: "var(--bf-primary)" }}
                          data-testid="prelaunch-notify-submit"
                        >
                          {subBusy ? "Saving…" : "Notify me"}
                        </Button>
                      </form>
                    )}
                  </div>
                )}
                {trailerEmbed && (
                  <div data-testid="prelaunch-trailer">
                    <div className="label-eyebrow mb-3 mt-4">Featured trailer</div>
                    <div className="relative aspect-video rounded overflow-hidden max-w-2xl" style={{ border: "1px solid var(--bf-border)" }}>
                      <iframe src={trailerEmbed} title="Trailer" allowFullScreen className="absolute inset-0 w-full h-full" />
                    </div>
                  </div>
                )}

                {/* YouTube videos */}
                {videosStatus === "loading" && (
                  <p className="text-sm mt-4" style={{ color: "var(--bf-text-muted)" }} data-testid="videos-loading">Loading videos…</p>
                )}
                {videosStatus === "ok" && videosData?.configured && videosData.videos?.length > 0 && (
                  <div data-testid="prelaunch-videos">
                    <div className="flex items-center justify-between mb-3 mt-4 flex-wrap gap-3">
                      <div>
                        <div className="label-eyebrow flex items-center gap-2"><Youtube className="w-3 h-3" /> From the author's channel</div>
                        {videosData.last_synced_at && (
                          <p className="text-xs mt-1" style={{ color: "var(--bf-text-muted)" }} data-testid="videos-last-synced">
                            Last synced {new Date(videosData.last_synced_at).toLocaleString()}
                          </p>
                        )}
                      </div>
                      {videosData.languages.length > 1 && (
                        <div className="flex gap-2 flex-wrap" data-testid="prelaunch-language-filter">
                          {videosData.languages.map((lang) => (
                            <button
                              key={lang}
                              onClick={() => {
                                setLanguage(lang);
                                const first = grouped[lang]?.[0];
                                if (first) setSelectedVideo(first);
                              }}
                              className="text-xs px-3 py-1.5 rounded transition-colors"
                              style={{
                                background: language === lang ? "var(--bf-primary)" : "var(--bf-surface)",
                                color: language === lang ? "white" : "var(--bf-text-muted)",
                                fontWeight: language === lang ? 600 : 500,
                              }}
                              data-testid={`prelaunch-language-${lang}`}
                            >
                              {LANG_NAMES[lang] || lang.toUpperCase()}
                              <span className="ml-2 opacity-70">{grouped[lang]?.length || 0}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <VideoStrip
                      videos={(language ? grouped[language] : videosData.videos) || []}
                      language={language || "all"}
                      onSelect={setSelectedVideo}
                      selectedId={selectedVideo?.video_id}
                    />
                    {selectedVideo && (
                      <div className="mt-6 grid md:grid-cols-[1.4fr_1fr] gap-6" data-testid="selected-video-panel">
                        <div className="relative aspect-video rounded overflow-hidden" style={{ border: "1px solid var(--bf-border)" }}>
                          <iframe
                            src={selectedVideo.embed_url}
                            title={selectedVideo.title}
                            allowFullScreen
                            className="absolute inset-0 w-full h-full"
                          />
                        </div>
                        <div>
                          <h3 className="font-serif text-2xl mb-2 leading-tight" data-testid="selected-video-title">{selectedVideo.title}</h3>
                          <p className="text-xs mb-3" style={{ color: "var(--bf-text-muted)" }}>
                            {selectedVideo.channel_title} · {selectedVideo.published_at ? new Date(selectedVideo.published_at).toLocaleDateString() : ""}
                          </p>
                          <p className="text-sm whitespace-pre-line line-clamp-[15]" data-testid="selected-video-description">
                            {selectedVideo.description || "(no description)"}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {videosStatus === "ok" && (!videosData?.configured || (videosData.videos?.length === 0)) && !trailerEmbed && (
                  <p className="text-sm mt-4" style={{ color: "var(--bf-text-muted)" }} data-testid="videos-empty">
                    No pre-launch videos shared yet — the author will plug them in soon.
                  </p>
                )}

                {/* Preview chapters — grouped by language with a tab switcher */}
                {preview_chapters && preview_chapters.length > 0 && (() => {
                  const byLang = preview_chapters.reduce((acc, c) => {
                    const l = c.language || book.language || "en";
                    (acc[l] = acc[l] || []).push(c);
                    return acc;
                  }, {});
                  const langs = Object.keys(byLang);
                  const activeLang = previewLang && byLang[previewLang] ? previewLang : langs[0];
                  return (
                    <div data-testid="public-preview">
                      <div className="flex items-center justify-between mt-6 mb-3 flex-wrap gap-3">
                        <div className="label-eyebrow">Read a preview</div>
                        {langs.length > 1 && (
                          <div className="flex gap-2 flex-wrap" data-testid="preview-language-tabs">
                            {langs.map((l) => (
                              <button
                                key={l}
                                onClick={() => setPreviewLang(l)}
                                className="text-xs px-3 py-1.5 rounded transition-colors"
                                style={{
                                  background: activeLang === l ? "var(--bf-primary)" : "var(--bf-surface)",
                                  color: activeLang === l ? "white" : "var(--bf-text-muted)",
                                  fontWeight: activeLang === l ? 600 : 500,
                                }}
                                data-testid={`preview-language-${l}`}
                              >
                                {LANG_NAMES[l] || l.toUpperCase()}
                                <span className="ml-2 opacity-70">{byLang[l].length}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="space-y-10">
                        {(byLang[activeLang] || []).map((c) => (
                          <article key={c.chapter_id} className="space-y-3" data-testid={`public-preview-chapter-${c.chapter_id}`}>
                            <h3 className="font-serif text-2xl">{c.title}</h3>
                            <div
                              className="prose-book max-w-none"
                              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(c.content || "<p><em>(empty)</em></p>", { USE_PROFILES: { html: true } }) }}
                            />
                          </article>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* POST-LAUNCH */}
          <AccordionItem
            value="postlaunch"
            className="rounded-lg overflow-hidden border-0"
            style={{ background: "white", border: "1px solid var(--bf-border)" }}
            data-testid="lifecycle-postlaunch"
          >
            <AccordionTrigger className="px-6 py-5 hover:no-underline" data-testid="lifecycle-postlaunch-trigger">
              <div className="flex items-center gap-3">
                {isPublished ? <ShoppingCart className="w-5 h-5" style={{ color: "var(--bf-primary)" }} /> : <Hourglass className="w-5 h-5" style={{ color: "var(--bf-accent)" }} />}
                <div className="text-left">
                  <h2 className="font-serif text-2xl">Post-launch</h2>
                  <p className="text-xs" style={{ color: "var(--bf-text-muted)" }}>
                    {isPublished ? "The book is out. Here's where to grab a copy." : "Coming soon."}
                  </p>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-6 pb-7" data-testid="lifecycle-postlaunch-content">
              <div className="pt-4 border-t" style={{ borderColor: "var(--bf-border)" }}>
                {!isPublished ? (
                  <div className="py-8 text-center" data-testid="postlaunch-upcoming">
                    <Hourglass className="w-10 h-10 mx-auto mb-4" style={{ color: "var(--bf-accent)" }} />
                    <p className="font-serif text-3xl mb-2">Upcoming</p>
                    <p className="text-sm" style={{ color: "var(--bf-text-muted)" }}>
                      {book.launch_date
                        ? `This book launches on ${book.launch_date}.`
                        : "This book is still in the writing or pre-launch phase."}
                    </p>
                    {book.buy_url && (
                      <a href={book.buy_url} target="_blank" rel="noreferrer" data-testid="postlaunch-preorder-btn">
                        <Button className="mt-5 text-white" style={{ background: "var(--bf-primary)" }}>
                          <ShoppingCart className="w-4 h-4 mr-2" /> Pre-order your copy
                        </Button>
                      </a>
                    )}
                  </div>
                ) : (
                  <div className="py-6 space-y-5" data-testid="postlaunch-available">
                    {book.isbn && (
                      <p className="text-sm" style={{ color: "var(--bf-text-muted)" }}>
                        <strong className="text-[color:var(--bf-text)]">ISBN:</strong> {book.isbn}
                      </p>
                    )}
                    {book.published_date && (
                      <p className="text-sm" style={{ color: "var(--bf-text-muted)" }}>
                        <strong className="text-[color:var(--bf-text)]">Published:</strong> {book.published_date}
                      </p>
                    )}
                    <div>
                      <div className="label-eyebrow mb-3">Where to buy</div>
                      {buyLinks.length === 0 ? (
                        <p className="text-sm" style={{ color: "var(--bf-text-muted)" }}>
                          No purchase links added yet.
                        </p>
                      ) : (
                        <div className="flex flex-wrap gap-3">
                          {buyLinks.map((link, idx) => (
                            <a key={link.url || idx} href={link.url} target="_blank" rel="noreferrer" data-testid={`postlaunch-buy-link-${idx}`}>
                              <Button
                                className={link.primary ? "text-white" : ""}
                                variant={link.primary ? "default" : "outline"}
                                style={link.primary ? { background: "var(--bf-primary)" } : {}}
                              >
                                <ShoppingCart className="w-4 h-4 mr-2" />
                                {link.label}
                              </Button>
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </main>

      <footer className="py-10 border-t text-center text-sm" style={{ borderColor: "var(--bf-border)", color: "var(--bf-text-muted)" }}>
        <span data-testid="public-share-url">{share_url}</span> · Published with <Link to="/" className="underline">Bookforge</Link>
      </footer>
    </div>
  );
}
